import { useQuery } from "@tanstack/react-query";

import { dataflowApi } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import { TaskTypeBadge } from "@/components/dataflow/TaskTypeBadge";

function fmtTime(raw?: string): string {
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function QueueRuntimeView() {
  const { setSelectedInstance } = useDataFlowStore();
  const { data, isLoading } = useQuery({
    queryKey: ["dataflow", "queue"],
    queryFn: () => dataflowApi.getQueue(),
    refetchInterval: 10000,
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-[var(--muted)]">Loading queue...</div>;
  }

  const slots = data?.slots || [];
  const running = data?.running_instances || [];
  const pending = data?.pending_instances || [];

  return (
    <div className="h-full overflow-auto p-3">
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <section className="rounded border border-[var(--border)] bg-[var(--panel)] p-3">
          <div className="mb-2 text-xs text-[var(--muted)]">Executors ({slots.length})</div>
          <div className="grid grid-cols-1 gap-2">
            {slots.map((slot) => (
              <div key={slot.id} className="flex items-center justify-between rounded border border-[var(--border)] px-2 py-1.5 text-xs">
                <span className="text-[var(--muted)]">Slot {slot.id}</span>
                <span className={slot.status === "running" ? "text-emerald-300" : "text-slate-300"}>
                  {slot.status}
                  {slot.instance_id ? ` · #${slot.instance_id}` : ""}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded border border-[var(--border)] bg-[var(--panel)] p-3">
          <div className="mb-2 flex items-center justify-between text-xs text-[var(--muted)]">
            <span>Running ({running.length})</span>
            <span>Queue Depth: {data?.depth || 0}</span>
          </div>
          <div className="space-y-1">
            {running.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedInstance(item.id)}
                className="flex w-full items-center justify-between rounded border border-[var(--border)] px-2 py-1.5 text-left text-xs hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-2">
                  <span className="text-slate-200">#{item.id}</span>
                  <span>·</span>
                  <TaskTypeBadge taskType={item.task_type} />
                </div>
                <span className="text-[var(--muted)]">{fmtTime(item.started_at)}</span>
              </button>
            ))}
            {running.length === 0 && (
              <div className="text-xs text-[var(--muted)]">No running instances.</div>
            )}
          </div>
        </section>

        <section className="rounded border border-[var(--border)] bg-[var(--panel)] p-3 xl:col-span-2">
          <div className="mb-2 text-xs text-[var(--muted)]">Pending Queue ({pending.length})</div>
          <div className="divide-y divide-[var(--border)] rounded border border-[var(--border)]">
            {pending.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedInstance(item.id)}
                className="grid w-full grid-cols-[90px_1fr_90px_70px_120px] gap-2 px-2 py-2 text-left text-xs hover:bg-white/[0.03]"
              >
                <span className="text-slate-200">#{item.id}</span>
                <span className="truncate text-slate-200">{item.name}</span>
                <TaskTypeBadge taskType={item.task_type} />
                <span className="text-sky-300">P{item.priority}</span>
                <span className="text-[var(--muted)]">{fmtTime(item.queued_at)}</span>
              </button>
            ))}
            {pending.length === 0 && (
              <div className="px-2 py-2 text-xs text-[var(--muted)]">Queue empty.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
